import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { user as userApi, motivos as motivosApi, ap as apApi, notas as notasApi } from '../services/api';
import { applyCustomColors } from '../services/colorPreferences';
import { getTurnoForDate } from '../utils/dateUtils';

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatDateStr = (year, month, day) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

export const useCalendarLogic = (user, toast) => {
    const [currentMonth, setCurrentMonth] = useState(() => {
        const saved = sessionStorage.getItem('calendar_view_month');
        return saved !== null ? parseInt(saved) : new Date().getMonth();
    });
    const [currentYear, setCurrentYear] = useState(() => {
        const saved = sessionStorage.getItem('calendar_view_year');
        return saved !== null ? parseInt(saved) : new Date().getFullYear();
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [conductor, setConductor] = useState(null);
    const [calendarData, setCalendarData] = useState({
        libranzas: [],
        vacaciones: [],
        quincenas: [],
        festivos: [],
        jornadas: [],
        shiftOverrides: {},
        exchangeInfo: {},
        motivos: {}
    });
    const [backendYearStats, setBackendYearStats] = useState(null);
    const [diasConMotivo, setDiasConMotivo] = useState([]);
    const [notasMes, setNotasMes] = useState([]);
    const [turnos, setTurnos] = useState([]);

    // AP State
    const [apBalance, setApBalance] = useState(null);
    const [apSolicitudes, setApSolicitudes] = useState([]);
    const [submittingAp, setSubmittingAp] = useState(false);

    // Navigation and persisting view
    useEffect(() => {
        sessionStorage.setItem('calendar_view_month', currentMonth.toString());
        sessionStorage.setItem('calendar_view_year', currentYear.toString());
    }, [currentMonth, currentYear]);

    const loadCalendarData = useCallback(async () => {
        if (!user || user.needsLink) return;

        try {
            setLoading(true);
            setError(null);

            const data = await userApi.getCalendario(currentYear);

            setConductor(data.conductor);
            setCalendarData({
                libranzas: data.libranzas || [],
                libranzasAnexo: data.libranzasAnexo || [],
                vacaciones: data.vacaciones || [],
                quincenas: data.quincenas || [],
                festivos: data.festivos || [],
                jornadas: data.jornadas || [],
                shiftOverrides: data.shiftOverrides || {},
                exchangeInfo: data.exchangeInfo || {},
                motivos: data.motivos || {}
            });
            setNotasMes(data.notas || []);

            if (data.yearStats) {
                setBackendYearStats(data.yearStats);
            }

            // Load master data (turnos)
            try {
                const turnosData = await motivosApi.getTurnos();
                setTurnos(applyCustomColors(turnosData));
            } catch (e) {
                console.warn('Could not load turnos master:', e);
            }

            // Load motivos data
            if (data.conductor?.id) {
                try {
                    const motivosData = await motivosApi.getDias(data.conductor.id, currentYear);
                    setDiasConMotivo(motivosData);
                } catch (e) {
                    console.warn('Could not load motivos:', e);
                    setDiasConMotivo([]);
                }

                // Load AP data
                try {
                    const [balanceData, solicitudesData] = await Promise.all([
                        apApi.getBalance(currentYear),
                        apApi.getSolicitudes(currentYear)
                    ]);
                    setApBalance(balanceData);
                    setApSolicitudes(solicitudesData);
                } catch (e) {
                    console.warn('Could not load AP data:', e);
                }
            }

        } catch (err) {
            console.error('Error loading calendar:', err);
            if (err.message.includes('conductor vinculado')) {
                setError('noLink');
            } else {
                setError(err.message);
                toast.error('Error al cargar el calendario');
            }
        } finally {
            setLoading(false);
        }
    }, [currentYear]);

    useEffect(() => {
        loadCalendarData();
    }, [loadCalendarData]);

    // Color change listener
    useEffect(() => {
        const handleColorChange = async () => {
            try {
                const turnosData = await motivosApi.getTurnos();
                setTurnos(applyCustomColors(turnosData));
            } catch (e) {
                console.warn('Could not reload turnos for color change:', e);
            }
        };

        window.addEventListener('calendarColorsChanged', handleColorChange);
        return () => window.removeEventListener('calendarColorsChanged', handleColorChange);
    }, []);

    const prevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(y => y - 1);
        } else {
            setCurrentMonth(m => m - 1);
        }
    };

    const nextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(y => y + 1);
        } else {
            setCurrentMonth(m => m + 1);
        }
    };

    const loadApData = async () => {
        try {
            const [balanceData, solicitudesData] = await Promise.all([
                apApi.getBalance(currentYear),
                apApi.getSolicitudes(currentYear)
            ]);
            setApBalance(balanceData);
            setApSolicitudes(solicitudesData);
        } catch (e) {
            console.warn('Could not load AP data:', e);
        }
    };

    const loadNotasData = async () => {
        try {
            const data = await userApi.getCalendario(currentYear);
            setNotasMes(data.notas || []);
        } catch (e) {
            console.warn('Could not reload Notas data:', e);
        }
    };

    const handleSolicitarAp = async (dateStr, tipo = 'ap') => {
        setSubmittingAp(true);
        try {
            await apApi.solicitar(dateStr, tipo);
            toast.success(tipo === 'ajuste_jornada' ? 'Ajuste de Jornada enviado' : 'Solicitud de A.P. enviada');
            await loadApData();
            return true;
        } catch (err) {
            toast.error(err.message || 'Error al solicitar');
            return false;
        } finally {
            setSubmittingAp(false);
        }
    };

    return {
        currentMonth,
        currentYear,
        loading,
        error,
        conductor,
        calendarData,
        backendYearStats,
        diasConMotivo,
        notasMes,
        turnos,
        apBalance,
        apSolicitudes,
        submittingAp,
        prevMonth,
        nextMonth,
        loadCalendarData,
        loadApData,
        loadNotasData,
        handleSolicitarAp,
        setCalendarData,
        MONTHS
    };
};
